import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Key, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Gift,
  User,
  Camera,
  Gem,
  Briefcase,
  Calendar,
  Home,
  PenTool,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';

// Keychain types
const KEYCHAIN_TYPES = [
  { value: 'inciso', label: 'Portachiavi inciso / Personalizzato', icon: PenTool },
  { value: 'con_foto', label: 'Portachiavi con foto', icon: Camera },
  { value: 'sagomato', label: 'Portachiavi sagomato / Forma custom', icon: Layers },
  { value: 'miniatura', label: 'Miniatura / Riproduzione oggetto', icon: Gem },
  { value: 'coordinato', label: 'Set coordinato (coppia/famiglia)', icon: Heart },
  { value: 'commemorativo', label: 'Commemorativo / In memoria', icon: Star },
  { value: 'aziendale', label: 'Gadget aziendale / Promozionale', icon: Briefcase },
  { value: 'collezione', label: 'Pezzo da collezione', icon: Gem },
  { value: 'altro', label: 'Altro', icon: Key },
];

// Material preferences
const MATERIALS = [
  { value: 'legno', label: 'Legno naturale / Intagliato', icon: Home },
  { value: 'resina', label: 'Resina epossidica / Cristallo', icon: Gem },
  { value: 'pelle', label: 'Pelle / Cuoio artigianale', icon: Layers },
  { value: 'metallo', label: 'Metallo (acciaio, ottone, rame)', icon: Key },
  { value: 'argento', label: 'Argento 925', icon: Sparkles },
  { value: 'oro', label: 'Oro / Placcato oro', icon: Star },
  { value: 'ceramica', label: 'Ceramica / Terracotta', icon: Gem },
  { value: 'tessuto', label: 'Tessuto / Feltro / Macramè', icon: Heart },
  { value: 'misto', label: 'Materiali combinati', icon: Layers },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Purpose/Occasion
const KEYCHAIN_PURPOSES = [
  { value: 'regalo_compleanno', label: 'Regalo di compleanno', icon: Gift },
  { value: 'regalo_coppia', label: 'Per la persona amata', icon: Heart },
  { value: 'matrimonio', label: 'Bomboniera / Matrimonio', icon: Calendar },
  { value: 'nascita', label: 'Nascita / Battesimo', icon: Star },
  { value: 'laurea', label: 'Laurea / Diploma', icon: Star },
  { value: 'commemorativo', label: 'In memoria di qualcuno', icon: Heart },
  { value: 'uso_personale', label: 'Per uso personale', icon: User },
  { value: 'aziendale', label: 'Gadget aziendale', icon: Briefcase },
  { value: 'evento', label: 'Evento speciale', icon: Calendar },
  { value: 'altro', label: 'Altro', icon: Key },
];

// Quantity options
const QUANTITY_OPTIONS = [
  { value: '1', label: '1 pezzo unico', description: 'Singolo esemplare' },
  { value: '2-5', label: '2-5 pezzi', description: 'Piccola serie' },
  { value: '6-10', label: '6-10 pezzi', description: 'Gruppo/Famiglia' },
  { value: '11-30', label: '11-30 pezzi', description: 'Piccolo evento' },
  { value: '31-50', label: '31-50 pezzi', description: 'Evento medio' },
  { value: '50+', label: '50+ pezzi', description: 'Grande evento / Azienda' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '10-25', label: '€10 - €25', description: 'Portachiavi semplice' },
  { value: '25-50', label: '€25 - €50', description: 'Lavorazione media' },
  { value: '50-100', label: '€50 - €100', description: 'Materiali pregiati' },
  { value: '100-200', label: '€100 - €200', description: 'Argento / Oro' },
  { value: '200+', label: '€200+', description: 'Pezzo d\'autore' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example keychains
const KEYCHAIN_EXAMPLES = [
  {
    idea: "Portachiavi con le impronte del mio cane",
    result: "Medaglietta in argento 925 con incisione laser delle impronte e nome",
    image: "https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=400&h=300&fit=crop",
    category: "Commemorativo"
  },
  {
    idea: "Set coordinato per matrimonio",
    result: "50 portachiavi in legno d'ulivo con iniziali sposi e data incisa",
    image: "https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=400&h=300&fit=crop",
    category: "Matrimonio"
  },
  {
    idea: "Miniatura della mia moto",
    result: "Riproduzione in resina colorata della Vespa del cliente con dettagli fedeli",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
    category: "Miniature"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho fatto realizzare 60 portachiavi in legno per il mio matrimonio. Ogni ospite ha apprezzato tantissimo questo ricordo unico.",
    author: "Laura e Marco",
    location: "Verona"
  },
  {
    text: "Il portachiavi con la foto del mio gatto è perfetto. La qualità della resina è incredibile, sembra una miniatura 3D.",
    author: "Chiara P.",
    location: "Bologna"
  },
  {
    text: "Per il 50° anniversario dei miei genitori ho fatto incidere le loro iniziali su argento. Un regalo che li ha commossi.",
    author: "Giovanni M.",
    location: "Napoli"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quanto costa un portachiavi personalizzato?",
    answer: "I prezzi variano in base al materiale e alla complessità: dai €15-25 per legno/resina semplici, €50-100 per pelle pregiata o metallo lavorato, fino a €150+ per argento o oro con incisioni dettagliate."
  },
  {
    question: "Posso ordinare grandi quantità per eventi?",
    answer: "Certamente! Molti artigiani offrono sconti per ordini multipli. Specifica la quantità nella richiesta e riceverai preventivi dedicati per bomboniere, gadget aziendali o regali di gruppo."
  },
  {
    question: "È possibile inserire una foto nel portachiavi?",
    answer: "Sì, con diverse tecniche: stampa in resina cristallina, incisione laser su metallo/legno, o miniportaritratti. L'artigiano ti consiglierà la soluzione migliore per la tua foto."
  },
  {
    question: "Quanto tempo ci vuole per la realizzazione?",
    answer: "Un portachiavi singolo richiede generalmente 1-2 settimane. Per ordini multipli o lavorazioni complesse (argento, miniature dettagliate) calcola 3-4 settimane."
  },
  {
    question: "Posso vedere un bozzetto prima della realizzazione?",
    answer: "Sì, la maggior parte degli artigiani invia un rendering digitale o uno schizzo per approvazione prima di procedere con la realizzazione definitiva."
  }
];

export default function PortachiaviRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    keychain_type: '',
    material: '',
    purpose: '',
    quantity: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Portachiavi Personalizzati | Artigiani Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `keychain-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi il portachiavi che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_keychain_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const keychainTypeLabel = KEYCHAIN_TYPES.find(t => t.value === formData.keychain_type)?.label || '';
      const materialLabel = MATERIALS.find(t => t.value === formData.material)?.label || '';
      const purposeLabel = KEYCHAIN_PURPOSES.find(p => p.value === formData.purpose)?.label || '';
      const quantityLabel = QUANTITY_OPTIONS.find(s => s.value === formData.quantity)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (keychainTypeLabel) details.push(`🔑  Tipo: ${keychainTypeLabel}`);
      if (materialLabel) details.push(`✨  Materiale: ${materialLabel}`);
      if (purposeLabel) details.push(`🎁  Occasione: ${purposeLabel}`);
      if (quantityLabel) details.push(`📦  Quantità: ${quantityLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${keychainTypeLabel} - ${materialLabel}`.trim() || 'Portachiavi personalizzato',
        description: fullDescription,
        category: 'Portachiavi',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('keychain-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Portachiavi Personalizzati | Artigiani Italiani | Rarifex</title>
        <meta name="description" content="Portachiavi personalizzati fatti a mano da artigiani italiani. Incisioni, foto, legno, resina, argento. Perfetti come regalo o bomboniera. Gratuito e senza impegno." />
        <meta name="keywords" content="portachiavi personalizzato, portachiavi inciso, portachiavi con foto, portachiavi legno, portachiavi argento, bomboniere portachiavi, regalo personalizzato" />
        <link rel="canonical" href="https://www.rarifex.com/portachiavi-personalizzati" />
        <meta property="og:title" content="Portachiavi Personalizzati | Artigiani Italiani | Rarifex" />
        <meta property="og:description" content="Portachiavi unici fatti a mano da artigiani italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/portachiavi-personalizzati" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 via-white to-teal-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Key className="w-4 h-4" />
                    Portachiavi Personalizzati
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Un piccolo oggetto,{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                      un grande significato
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artigiani italiani creano il portachiavi dei tuoi sogni.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Incisioni, foto, materiali pregiati. Perfetto come regalo unico o bomboniera memorabile.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-emerald-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="keychain-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Portachiavi con le iniziali per anniversario"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="keychain-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il portachiavi che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Forma, scritte da incidere, colori, dettagli speciali. Se hai foto di riferimento, potrai allegarle sotto."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="keychain-description-textarea"
                        />
                      </div>
                      
                      {/* Type & Material */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di portachiavi
                          </Label>
                          <select
                            value={formData.keychain_type}
                            onChange={(e) => handleInputChange('keychain_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            data-testid="keychain-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {KEYCHAIN_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Materiale preferito
                          </Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            data-testid="keychain-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIALS.map(mat => (
                              <option key={mat.value} value={mat.value}>{mat.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Quantity */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Occasione</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            data-testid="keychain-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {KEYCHAIN_PURPOSES.map(purpose => (
                              <option key={purpose.value} value={purpose.value}>{purpose.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Quantità</Label>
                          <select
                            value={formData.quantity}
                            onChange={(e) => handleInputChange('quantity', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            data-testid="keychain-quantity-select"
                          >
                            <option value="">Seleziona...</option>
                            {QUANTITY_OPTIONS.map(qty => (
                              <option key={qty.value} value={qty.value}>
                                {qty.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget (per pezzo)</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            data-testid="keychain-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            data-testid="keychain-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Foto di riferimento o ispirazioni
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="keychain-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto o ispirazioni ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-200"
                        data-testid="keychain-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Key className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                I portachiavi standard non raccontano nulla
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-emerald-400">✗</span>
                  I portachiavi in commercio sono tutti uguali
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-emerald-400">✗</span>
                  Vorresti un regalo unico ma non sai dove trovarlo
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-emerald-400">✗</span>
                  Le bomboniere classiche finiscono dimenticate in un cassetto
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Per un oggetto che si porta sempre con sé, serve un tocco personale.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Un portachiavi{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  che racconta la tua storia
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Artigiani italiani trasformano le tue idee in piccoli capolavori da portare sempre con te.
                <br />
                <strong className="text-slate-800">Tu immagini, loro creano.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi la tua idea</h3>
                  <p className="text-slate-600">Racconta cosa vorresti: forma, incisioni, materiale</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-teal-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Artigiani ti propongono</h3>
                  <p className="text-slate-600">Ricevi bozzetti e preventivi da esperti del settore</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il tuo artigiano</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a portachiavi unico
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri artigiani
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {KEYCHAIN_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-emerald-500 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-emerald-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 3 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi il portachiavi</h3>
                    <p className="text-slate-600">
                      Racconta cosa vuoi: scritte da incidere, foto da inserire, forma e materiale. Allega immagini di riferimento se le hai.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte</h3>
                    <p className="text-slate-600">
                      Artigiani specializzati vedono la tua richiesta e ti inviano bozzetti, campioni di materiale e preventivi dettagliati.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli l'artigiano perfetto</h3>
                    <p className="text-slate-600">
                      Confronta stili e prezzi, chatta per definire i dettagli. Nessun obbligo, scegli solo se sei convinto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere un portachiavi artigianale
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-emerald-50">
                  <CheckCircle className="w-8 h-8 text-emerald-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Pezzo unico</h3>
                    <p className="text-sm text-slate-600">Nessuno avrà mai lo stesso identico</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-teal-50">
                  <Sparkles className="w-8 h-8 text-teal-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Personalizzazione totale</h3>
                    <p className="text-sm text-slate-600">Nome, data, foto, forma custom</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Qualità artigianale</h3>
                    <p className="text-sm text-slate-600">Materiali pregiati, lavorazione curata</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-emerald-400 text-emerald-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-semibold text-emerald-600">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`keychain-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-emerald-50 to-teal-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Key className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto a creare il tuo portachiavi unico?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                Gli artigiani italiani aspettano la tua idea.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-200"
                data-testid="keychain-cta-final-button"
              >
                <Key className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dagli artigiani, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/portachiavi-personalizzati')}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
